<template>
    <div style="background: white">
        <filters-wrapper class="mb-4" :has-active-filters="hasActiveFilters" @submit="applyFilter" @reset="resetFilter">
            <v-form class="mb-6">
                <v-row dense>
                    <v-col cols="12" md="4">
                        <daterange
                            :start-date-label="$t('date_from')"
                            :end-date-label="$t('date_to')"
                            :single-line="false"
                            :hide-details="false"
                            :min-start-date="null"
                            same-date
                            :_start-date="filters.dueDateFrom"
                            :_end-date="filters.dueDateTo"
                            data-cy="date-range"
                            :clearable="true"
                            @change-start-date="filters.dueDateFrom = $event"
                            @change-end-date="filters.dueDateTo = $event"
                        />
                    </v-col>
                    <v-col cols="12" md="4">
                        <daterange
                            :start-date-label="$t('pay_date_from')"
                            :end-date-label="$t('pay_date_to')"
                            :single-line="false"
                            :hide-details="false"
                            :min-start-date="null"
                            same-date
                            :_start-date="filters.payDateFrom"
                            :_end-date="filters.payDateTo"
                            data-cy="pay-date-range"
                            :clearable="true"
                            @change-start-date="filters.payDateFrom = $event"
                            @change-end-date="filters.payDateTo = $event"
                        />
                    </v-col>
                    <v-col cols="12" md="2">
                        <v-text-field
                            v-model="filters.referenceNumber"
                            :label="$t('reference_number')"
                            hide-details
                            variant="outlined"
                            color="primary"
                            data-cy="name"
                            class="mb-4"
                        />
                    </v-col>
                    <v-col cols="12" md="2">
                        <v-text-field
                            v-model="filters.invoiceName"
                            :label="$t('invoices.name')"
                            hide-details
                            variant="outlined"
                            color="primary"
                            data-cy="name"
                            class="mb-4"
                        />
                    </v-col>
                    <v-col cols="12" md="2">
                        <private-client-and-companies-autocomplete
                            ref="customerAutocomplete"
                            v-model="selectedClient"
                            :show-icon="false"
                            :label="$t('client')"
                            :relationship="['CLIENT']"
                            disable-persistent-placeholder
                            :rules="[]"
                            @change="onClientNameFilterChange"
                        />
                    </v-col>
                    <v-col cols="6" md="2">
                        <supplier-select-autocomplete
                            ref="supplierAutocomplete"
                            v-model="selectedSupplier"
                            :rules="[]"
                            :label="$t('supplier_name')"
                            disable-persistent-placeholder
                            @change="onSupplierNameFilterChange"
                        />
                    </v-col>
                    <v-col cols="12" md="2">
                        <v-select
                            v-model="filters.currencyCode"
                            :items="currencies"
                            hide-details
                            clearable
                            :label="`${$t('original_currency')}`"
                        />
                    </v-col>
                    <v-col cols="12" md="2">
                        <v-select
                            v-model="filters.paymentStatus"
                            :items="statusItems"
                            hide-details
                            clearable
                            multiple
                            :label="`${$t('status')}`"
                        />
                    </v-col>
                </v-row>
            </v-form>
        </filters-wrapper>
        <v-row no-gutters>
            <v-col cols="12" sm="3">
                <v-select
                    v-if="isTourOperator"
                    v-model="invoiceType"
                    :items="invoiceTypes"
                    :label="$t('invoice_type')"
                    variant="solo"
                />
            </v-col>
            <v-col cols="12" sm="3" offset-sm="6" class="d-flex justify-end">
                <v-btn color="primary" variant="text" @click="toggleActive">
                    <v-icon start> mdi-archive-arrow-{{ active ? 'down' : 'up' }} </v-icon>
                    {{ $t(active ? 'archive' : 'standard_mode') }}
                </v-btn>
            </v-col>
        </v-row>
        <v-data-table-server
            v-if="invoicesInfo.invoices && invoicesInfo.invoices.length"
            v-model:page="activePage"
            v-model:items-per-page="itemsPerPage"
            :headers="headers"
            :items="invoicesInfo.invoices"
            :items-length="invoicesInfo.invoicesCount"
            :items-per-page-options="[
                {value: itemsPerPage, title: itemsPerPage},
                {value: 10, title: '10'},
                {value: 25, title: '25'},
                {value: 50, title: '50'},
                {value: 100, title: '100'},
            ]"
            @update:options="getInvoices"
        >
            <template v-slot:item.invoiceName="{item}">
                <div class="invoice-name-block cursor-pointer">
                    <a @click="printInvoice(item.invoiceId, item.invoiceName)">
                        {{ item.invoiceName }}
                        <v-icon color="blue-darken-1">{{ fileIcon }}</v-icon>
                    </a>
                </div>
            </template>
            <template v-slot:item.paymentStatus="{item}">
                <div>
                    {{ $t(`paymentStatus.${item.paymentStatus}`) }}
                </div>
            </template>
            <template v-slot:item.amount="{item}">
                <div>
                    {{ $filters.priceFormat(item.amount, item.amountCurrency) }}
                </div>
            </template>
            <template v-slot:item.amountToPay="{item}">
                <div>
                    {{ $filters.priceFormat(item.amountToPay, item.amountToPayCurrency) }}
                </div>
            </template>
            <template v-slot:item.billDate="{item}">
                <div>
                    {{ $filters.dateFormat(item.billDate) }}
                </div>
            </template>
            <template v-slot:item.dueDate="{item}">
                <div>
                    {{ $filters.dateFormat(item.dueDate) }}
                </div>
            </template>
            <template v-slot:item.pay="{item}">
                <v-btn
                    v-if="['BILL', 'PARTIAL_PAID'].includes(item.paymentStatus)"
                    color="primary"
                    size="small"
                    elevation="0"
                    @click="pay(item)"
                >
                    <v-icon start size="small"> mdi-plus-circle-multiple-outline </v-icon>
                    <span class="text-caption">
                        {{ $t('pay') }}
                    </span>
                </v-btn>
            </template>
            <template v-slot:item.payDate="{item}">
                <div>
                    {{ $filters.dateFormat(item.payDate) }}
                </div>
            </template>
            <template v-slot:item.requestReference="{item}">
                <nuxt-link
                    no-prefetch
                    :to="{name: 'order-details', params: {id: +item.requestReference}}"
                    @click.native.stop
                >
                    <div
                        v-if="item.requestReference"
                        class="text-no-wrap font-weight-medium"
                        v-text="+item.requestReference"
                    />
                </nuxt-link>
            </template>
        </v-data-table-server>
        <div v-else class="pt-5">
            {{ $t('invoices.empty_message') }}
        </div>
        <div v-if="canIssueInvoice" class="d-flex justify-end full-width">
            <v-btn class="mb-2" color="primary" @click="issueInvoices(invoiceType)">
                {{ $t('invoices.get_invoice_button') }}
            </v-btn>
        </div>
    </div>
</template>

<script lang="ts">
    import {Component, Prop, Vue, Emit, Watch} from 'vue-facing-decorator'
    import {EventBus, ISSUE_INVOICES_MODAL_EVENT, PAY_ORDER_EVENT} from '@/utils/event-bus'
    import {downloadData} from '@/utils/helpers'
    import InvoicesIssueModal from '@/components/modals/InvoicesIssueModal.vue'
    import PersonSelectAutocomplete from '~/components/search/forms/PersonSelectAutocomplete.vue'
    import Daterange from '~/components/search/forms/Daterange.vue'
    import PrivateClientAndCompaniesAutocomplete from '~/components/search/forms/PrivateClientAndCompaniesAutocomplete.vue'
    import SupplierSelectAutocomplete from '~/components/search/forms/SupplierSelectAutocomplete.vue'

    @Component({
        components: {
            SupplierSelectAutocomplete,
            PrivateClientAndCompaniesAutocomplete,
            Daterange,
            PersonSelectAutocomplete,
            InvoicesIssueModal,
        },
    })
    export class InvoicesList extends Vue {
        @Prop() requestInfo
        @Prop({type: Boolean, default: false}) showFilters

        invoicesInfo = {}
        isLoading = false
        invoiceType = null
        active = true

        filters = {}
        itemsPerPage = 10
        activePage = 1
        selectedClient = null
        selectedSupplier = null

        created() {
            if (this.$conf.public.account.licenseeForCorporateCompany) {
                this.invoiceType = 'SUPPLIER'
            } else this.invoiceType = this.requestInfo.invoiceType
        }

        @Watch('invoiceType')
        onInvoiceTypeChange() {
            this.activePage = 1
            this.getInvoices({itemsPerPage: this.itemsPerPage, page: this.activePage})
        }

        @Emit()
        changeLoading(isLoading) {
            this.isLoading = isLoading
            return isLoading
        }

        async getInvoices({itemsPerPage, page}) {
            this.changeLoading(true)
            try {
                this.invoicesInfo = await this.$api.invoices.get({
                    ...(this.requestInfo.objectId && {orderId: this.requestInfo.objectId}),
                    ...(this.requestInfo.companyId && {companyId: this.requestInfo.companyId}),
                    invoiceType: this.invoiceType,
                    active: this.active,
                    ...clean(this.filters),
                    limit: itemsPerPage,
                    offset: itemsPerPage * (page - 1),
                })
            } catch (error) {
                if (this.snackbar) {
                    this.snackbar.message = error.response?.data?.errors?.[0]?.message
                    this.snackbar.show = true
                }
            } finally {
                this.changeLoading(false)
            }
        }

        async pay(item) {
            EventBus.$emit(PAY_ORDER_EVENT, {
                orderId: this.requestInfo.orderId,
                objectId: this.requestInfo.objectId,
                invoiceId: item.invoiceId,
                invoiceType: this.invoiceType,
            })
        }

        toggleActive() {
            this.active = !this.active
            this.activePage = 1
            this.getInvoices({itemsPerPage: this.itemsPerPage, page: this.activePage})
        }

        applyFilter() {
            this.activePage = 1
            this.getInvoices({itemsPerPage: this.itemsPerPage, page: this.activePage})
        }

        resetFilter() {
            this.selectedSupplier = null
            this.selectedClient = null
            this.activePage = 1
            this.filters = {}
            this.getInvoices({itemsPerPage: this.itemsPerPage, page: this.activePage})
        }

        onClientNameFilterChange(val) {
            this.filters = {
                ...this.filters,
                customerName: val ? this.$refs.customerAutocomplete.inputText(val, null, true) : undefined,
            }
        }

        onSupplierNameFilterChange(val) {
            this.filters = {...this.filters, supplierName: val?.name}
        }

        get headers() {
            let headers = [{title: this.$t('invoices.name'), align: 'left', value: 'invoiceName'}]

            if (this.showFilters) {
                headers = [...headers, {title: this.$t('client_name'), align: 'left', value: 'customerName'}]
                headers = [...headers, {title: this.$t('supplier_name'), align: 'left', value: 'supplierName'}]
                headers = [...headers, {title: this.$t('order_number'), value: 'requestReference'}]
            }

            headers = [
                ...headers,
                {title: this.$t('invoices.status'), align: 'left', value: 'paymentStatus'},
                {title: this.$t('invoices.amount'), align: 'left', value: 'amount'},
                {title: this.$t('invoices.amount_to_pay'), align: 'left', value: 'amountToPay'},
                {title: this.$t('invoices.bill_date'), align: 'left', value: 'billDate'},
                {title: this.$t('invoices.due_date'), align: 'left', value: 'dueDate'},
            ]

            if ((this.isTO1 || authStore.isTO2 || authStore.isCorporate) && this.requestInfo.orderId) {
                headers.push({title: this.$t('pay'), value: 'pay', align: 'center'})
            }

            headers.push({title: this.$t('invoices.pay_date'), align: 'left', value: 'payDate'})
            return headers
        }

        get isTO1() {
            return authStore.isTO1
        }

        get invoiceTypes() {
            if (this.$conf.public.account.licenseeForCorporateCompany) {
                return [{title: this.$t('invoices.supplier'), value: 'SUPPLIER'}]
            }

            return [
                {title: this.$t('invoices.supplier'), value: 'SUPPLIER'},
                {title: this.$t('invoices.client'), value: 'CUSTOMER'},
            ]
        }

        async printInvoice(invoiceId, invoiceName) {
            try {
                const data = await this.$api.printInvoice.get({invoiceId})
                downloadData(data, `${invoiceName}.${this.invoicesInfo.reportFileType?.toLowerCase() || 'pdf'}`)
            } catch (error) {
                if (this.snackbar) {
                    this.snackbar.message = error.response?.data?.errors?.[0]?.message
                    this.snackbar.show = true
                }
            }
        }

        get isAgency() {
            return authStore.isAgency
        }

        get isTourOperator() {
            return authStore.isTourOperator
        }

        @Emit()
        issueInvoices(invoiceType) {
            EventBus.$emit(ISSUE_INVOICES_MODAL_EVENT, {
                orderId: this.requestInfo.orderId,
                objectId: this.requestInfo.objectId,
                invoiceType: invoiceType,
                order: this.requestInfo.order,
            })
        }

        get canIssueInvoice() {
            const failed = ['NO_BILL', 'NO_FULLY_BILL']

            if (this.invoiceType == 'CUSTOMER') {
                return failed.includes(this.requestInfo?.order?.customerPaymentStatus)
            }

            if (this.invoiceType == 'SUPPLIER') {
                return failed.includes(this.requestInfo?.order?.supplierPaymentStatus)
            }

            return false
        }

        get fileIcon() {
            return (
                this.fileFormats.find(f => f.extension === this.invoicesInfo.reportFileType?.toLowerCase())?.icon ||
                'mdi-file-pdf-box'
            )
        }

        get fileFormats() {
            return [
                {
                    format: 'PDF',
                    icon: 'mdi-file-pdf-box',
                    extension: 'pdf',
                },
                {
                    format: 'Excel',
                    icon: 'mdi-file-excel',
                    extension: 'xlsx',
                },
            ]
        }

        get hasActiveFilters() {
            return Object.keys(clean(this.filters)).length !== 0
        }

        get currencies() {
            return Object.values(this.$conf.public.currency.available).filter(currency =>
                Object.keys(runtimeStore.exchangeRates).includes(currency)
            )
        }

        get statusItems() {
            return [
                'PAID',
                'BILL',
                'IN_TRANSIT',
                'OVERDUE',
                'NO_FULLY_BILL',
                'CANCELED',
                'PARTIAL_PAID',
                'INVOICE_WAITING',
            ].map(el => ({
                value: el,
                title: this.$t(`paymentStatus.${el}`),
            }))
        }
    }

    export default InvoicesList
</script>

<style scoped>
    .invoice-name-block {
        max-width: 150px;
    }
</style>
